import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { LIFECYCLE_CODES, SUPPORT_MAILTO } from '../../../Account/lifecycle/lifecycle.constants'
import { canSendRecoveryLink } from '../../../Account/lifecycle/lifecycle.utils'
import { requestAccountReactivation } from '../../../Account/lifecycle/lifecycle.api'

export function SignupLifecycleNotice({ code, apiMessage, email, onSent }) {
  const { formatMessage: trans } = useIntl()
  const history = useHistory()
  const [isSending, setIsSending] = useState(false)
  const [sentMessage, setSentMessage] = useState('')

  if (!code) return null

  const handleRecovery = async () => {
    if (isSending || !email) return
    setIsSending(true)
    const result = await requestAccountReactivation(email)
    const generic = result.message || trans({ id: 'lifecycle_recovery_generic', defaultMessage: 'If an account can be recovered, we sent instructions to this email.' })
    setSentMessage(generic)
    onSent?.(generic)
    setIsSending(false)
  }

  const title =
    code === LIFECYCLE_CODES.ACTIVE_ACCOUNT_EXISTS
      ? trans({ id: 'lifecycle_account_already_exists', defaultMessage: 'Account already exists' })
      : code === LIFECYCLE_CODES.ACCOUNT_SUSPENDED
        ? trans({ id: 'lifecycle_account_suspended_title', defaultMessage: 'This account is suspended' })
        : code === LIFECYCLE_CODES.DELETION_PENDING
          ? trans({ id: 'lifecycle_deletion_pending_title', defaultMessage: 'A deletion is pending' })
          : trans({ id: 'lifecycle_reactivation_required_title', defaultMessage: 'This identity can be recovered' })

  const body =
    code === LIFECYCLE_CODES.ACTIVE_ACCOUNT_EXISTS
      ? trans({ id: 'lifecycle_account_already_exists_body', defaultMessage: 'Sign in or reset your password. A used email never creates another company.' })
      : code === LIFECYCLE_CODES.ACCOUNT_SUSPENDED
        ? trans({ id: 'lifecycle_account_suspended_body', defaultMessage: 'This account cannot be reactivated from here. Contact support.' })
        : code === LIFECYCLE_CODES.DELETION_PENDING
          ? trans({ id: 'lifecycle_deletion_pending_signup_body', defaultMessage: 'An account with this email has a deletion pending. We can send instructions to recover it.' })
          : trans({ id: 'lifecycle_reactivation_required_body', defaultMessage: 'An existing identity can be recovered. We can send a recovery link to this email.' })

  return (
    <div className={`lifecycle-notice lifecycle-notice--${code}`} role="alert" data-lifecycle-code={code}>
      <strong>{title}</strong>
      <p className="lifecycle-notice__text">{body}</p>
      {apiMessage ? <p className="lifecycle-notice__api">{apiMessage}</p> : null}
      {sentMessage ? <p className="lifecycle-notice__api">{sentMessage}</p> : null}
      {code === LIFECYCLE_CODES.ACTIVE_ACCOUNT_EXISTS ? (
        <div className="lifecycle-notice__actions">
          <button type="button" className="primary-button" onClick={() => history.push('/')}>
            {trans({ id: 'lifecycle_login', defaultMessage: 'Login' })}
          </button>
          <button type="button" className="secondary-button" onClick={() => history.push('/forgot_password')}>
            {trans({ id: 'lifecycle_forgot_password', defaultMessage: 'Forgot Password' })}
          </button>
        </div>
      ) : null}
      {canSendRecoveryLink(code) ? (
        <button type="button" className="primary-button" disabled={isSending || !email} onClick={handleRecovery}>
          {isSending ? trans({ id: 'loading', defaultMessage: 'Loading…' }) : trans({ id: 'lifecycle_send_recovery_link', defaultMessage: 'Send recovery link' })}
        </button>
      ) : null}
      {code === LIFECYCLE_CODES.ACCOUNT_SUSPENDED ? (
        <a className="primary-button lifecycle-notice__support" href={SUPPORT_MAILTO}>
          {trans({ id: 'lifecycle_contact_support', defaultMessage: 'Contact support' })}
        </a>
      ) : null}
    </div>
  )
}
